import React, { useEffect, useState, useMemo } from 'react';
import {
  Layout,
  Table,
  Button,
  Card,
  Input,
  message,
  Space,
  Tag,
  Row,
  Col,
  Switch,
  Tooltip,
  Alert,
  Typography,
  Popconfirm
} from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
  KeyOutlined,
  ApiOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  SearchOutlined,
  SaveOutlined,
  CheckOutlined
} from '@ant-design/icons';
import api from '../api';
import Sidebar from './Sidebar';
import ChangePasswordModal from './ChangePasswordModal';
import './SuperadminClients.css';

const { Content } = Layout;
const { Title, Text } = Typography;

export default function SuperadminZkteco() {
  const [collapsed, setCollapsed] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [savingMaster, setSavingMaster] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [revealingPassword, setRevealingPassword] = useState(false);

  const handlePasswordVisibilityChange = async (visible) => {
    if (visible) {
      if (masterConfig.password === '••••••••') {
        try {
          setRevealingPassword(true);
          const res = await api.post('/superadmin/zkteco/reveal-password');
          if (res.data?.success && res.data.password) {
            setMasterConfig((prev) => ({ ...prev, password: res.data.password }));
          }
        } catch (e) {
          message.error('Failed to reveal password');
        } finally {
          setRevealingPassword(false);
        }
      }
      setIsPasswordVisible(true);
    } else {
      setIsPasswordVisible(false);
    }
  };

  // Master server configuration state
  const [masterConfig, setMasterConfig] = useState({
    url: 'http://15.206.144.225:8081/',
    username: 'admin',
    password: '••••••••',
    companyId: ''
  });

  // Organizations list
  const [organizations, setOrganizations] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchText, setSearchText] = useState('');

  const loadOverview = async () => {
    try {
      setLoading(true);
      const res = await api.get('/superadmin/zkteco/overview');
      if (res.data?.success) {
        if (res.data.masterConfig) {
          setMasterConfig({
            url: res.data.masterConfig.url || 'http://15.206.144.225:8081/',
            username: res.data.masterConfig.username || 'admin',
            password: res.data.masterConfig.password || '••••••••',
            companyId: res.data.masterConfig.companyId || ''
          });
        }
        setOrganizations(res.data.organizations || []);
      } else {
        message.error(res.data?.message || 'Failed to load ZKTeco overview');
      }
    } catch (e) {
      message.error(e.response?.data?.message || 'Failed to fetch ZKTeco data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOverview();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.clear();
    window.location.href = '/';
  };

  const handleSaveMasterConfig = async () => {
    try {
      setSavingMaster(true);
      const res = await api.put('/superadmin/zkteco/master-config', masterConfig);
      if (res.data?.success) {
        message.success(res.data.message || 'Master configuration saved successfully');
        if (res.data.masterConfig) {
          setMasterConfig(res.data.masterConfig);
        }
      } else {
        message.error(res.data?.message || 'Failed to save master configuration');
      }
    } catch (e) {
      message.error(e.response?.data?.message || 'Failed to update master configuration');
    } finally {
      setSavingMaster(false);
    }
  };

  const handleToggleSingle = async (orgId, currentActive) => {
    try {
      setTogglingId(orgId);
      const newActive = !currentActive;
      const res = await api.put(`/superadmin/zkteco/toggle/${orgId}`, { active: newActive });
      if (res.data?.success) {
        setOrganizations((prev) =>
          prev.map((org) => (org.id === orgId ? { ...org, active: newActive } : org))
        );
        message.success(res.data.message || `Organization ${newActive ? 'activated' : 'deactivated'}`);
      } else {
        message.error(res.data?.message || 'Failed to update status');
      }
    } catch (e) {
      message.error(e.response?.data?.message || 'Failed to update status');
    } finally {
      setTogglingId(null);
    }
  };

  const handleBulkAction = async (actionType) => {
    if (!selectedRowKeys.length) {
      message.warning('Please select at least one organization');
      return;
    }

    try {
      setBulkLoading(true);
      const payload = {
        orgIds: selectedRowKeys,
        active: actionType === 'activate' ? true : actionType === 'deactivate' ? false : undefined,
        applyMasterCredentials: actionType === 'applyCredentials'
      };

      const res = await api.post('/superadmin/zkteco/bulk-update', payload);
      if (res.data?.success) {
        message.success(res.data.message || 'Bulk update completed successfully');
        loadOverview();
        setSelectedRowKeys([]);
      } else {
        message.error(res.data?.message || 'Bulk update failed');
      }
    } catch (e) {
      message.error(e.response?.data?.message || 'Bulk update failed');
    } finally {
      setBulkLoading(false);
    }
  };

  const filteredOrgs = useMemo(() => {
    if (!searchText) return organizations;
    const q = searchText.toLowerCase().trim();
    return organizations.filter(
      (o) =>
        String(o.name || '').toLowerCase().includes(q) ||
        String(o.id || '').includes(q) ||
        String(o.phone || '').includes(q)
    );
  }, [organizations, searchText]);

  const activeCount = useMemo(() => organizations.filter((o) => o.active).length, [organizations]);

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 70,
      render: (id) => <Text strong style={{ color: '#64748b' }}>#{id}</Text>
    },
    {
      title: 'Organization Name',
      dataIndex: 'name',
      render: (name, rec) => (
        <div>
          <div style={{ fontWeight: 600, color: '#1e293b' }}>{name}</div>
          {rec.phone && <div style={{ fontSize: 12, color: '#64748b' }}>{rec.phone}</div>}
        </div>
      )
    },
    {
      title: 'ZKTeco Sync Status',
      dataIndex: 'active',
      width: 160,
      render: (active) =>
        active ? (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            Active Sync
          </Tag>
        ) : (
          <Tag color="default" icon={<CloseCircleOutlined />}>
            Inactive
          </Tag>
        )
    },
    {
      title: 'Configured Server URL',
      dataIndex: 'configuredUrl',
      render: (url) => (
        <Text style={{ fontSize: 13, color: url ? '#2563eb' : '#94a3b8' }}>
          {url || masterConfig.url || 'Default Central Server'}
        </Text>
      )
    },
    {
      title: 'Action',
      key: 'action',
      width: 140,
      align: 'center',
      render: (_, rec) => (
        <Tooltip title={rec.active ? 'Click to Disable Sync' : 'Click to Enable Sync'}>
          <Switch
            checked={rec.active}
            loading={togglingId === rec.id}
            onChange={() => handleToggleSingle(rec.id, rec.active)}
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseCircleOutlined />}
          />
        </Tooltip>
      )
    }
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys)
  };

  return (
    <Layout className="superadmin-clients-root">
      <Sidebar collapsed={collapsed} />
      <Layout className={`superadmin-clients-main${collapsed ? ' is-collapsed' : ''}`}>
        <header className="superadmin-clients-topbar">
          <div className="superadmin-clients-topbar__left">
            {React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, {
              className: 'superadmin-clients-trigger',
              onClick: () => setCollapsed(!collapsed)
            })}
            <div className="superadmin-clients-topbar__titles">
              <h1>ZKTeco Biometric Integration</h1>
              <p>Manage central Easy TimePro server credentials and organization-level attendance sync</p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Button type="text" icon={<KeyOutlined />} onClick={() => setChangePasswordOpen(true)}>
              Change Password
            </Button>
            <Button type="text" icon={<LogoutOutlined />} onClick={handleLogout}>
              Log out
            </Button>
          </div>
        </header>

        <Content className="superadmin-clients-content" style={{ padding: '24px 32px' }}>
          {/* Top Section: Central Master Server Credentials */}
          <Card
            title={
              <Space>
                <ApiOutlined style={{ color: '#2563eb', fontSize: 18 }} />
                <span style={{ fontWeight: 700, fontSize: 16 }}>Central ZKTeco / Easy TimePro Server Settings</span>
              </Space>
            }
            style={{ borderRadius: 12, marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}
          >
            <Alert
              type="info"
              showIcon
              message="Master Configuration"
              description="These server credentials serve as the central master configuration for all organizations. When activating new orgs or pushing updates, organizations will automatically inherit this server URL and credentials."
              style={{ marginBottom: 20, borderRadius: 8 }}
            />

            <Row gutter={[16, 16]}>
              <Col xs={24} md={8}>
                <label style={{ display: 'block', fontWeight: 600, color: '#334155', marginBottom: 6 }}>
                  API Server URL
                </label>
                <Input
                  placeholder="e.g. http://15.206.144.225:8081/"
                  value={masterConfig.url}
                  onChange={(e) => setMasterConfig({ ...masterConfig, url: e.target.value })}
                  style={{ borderRadius: 8 }}
                />
              </Col>
              <Col xs={24} md={8}>
                <label style={{ display: 'block', fontWeight: 600, color: '#334155', marginBottom: 6 }}>
                  Username
                </label>
                <Input
                  placeholder="admin"
                  value={masterConfig.username}
                  onChange={(e) => setMasterConfig({ ...masterConfig, username: e.target.value })}
                  style={{ borderRadius: 8 }}
                />
              </Col>
              <Col xs={24} md={8}>
                <label style={{ display: 'block', fontWeight: 600, color: '#334155', marginBottom: 6 }}>
                  Password
                </label>
                <Input.Password
                  placeholder="••••••••"
                  value={masterConfig.password}
                  onChange={(e) => setMasterConfig({ ...masterConfig, password: e.target.value })}
                  visibilityToggle={{
                    visible: isPasswordVisible,
                    onVisibleChange: handlePasswordVisibilityChange
                  }}
                  disabled={revealingPassword}
                  style={{ borderRadius: 8 }}
                />
              </Col>
            </Row>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={savingMaster}
                onClick={handleSaveMasterConfig}
                style={{ borderRadius: 8, padding: '0 24px' }}
              >
                Save Server Settings
              </Button>
            </div>
          </Card>

          {/* Stats Bar */}
          <div className="superadmin-clients-stats" style={{ marginBottom: 24 }}>
            <div className="sa-stat-card sa-stat-card--accent">
              <div className="sa-stat-card__label">Total Organizations</div>
              <div className="sa-stat-card__value">{organizations.length}</div>
            </div>
            <div className="sa-stat-card sa-stat-card--success">
              <div className="sa-stat-card__label">ZKTeco Active</div>
              <div className="sa-stat-card__value">{activeCount}</div>
            </div>
            <div className="sa-stat-card sa-stat-card--warn">
              <div className="sa-stat-card__label">ZKTeco Inactive</div>
              <div className="sa-stat-card__value">{organizations.length - activeCount}</div>
            </div>
            <div className="sa-stat-card">
              <div className="sa-stat-card__label">Selected For Action</div>
              <div className="sa-stat-card__value" style={{ color: '#2563eb' }}>
                {selectedRowKeys.length}
              </div>
            </div>
          </div>

          {/* Organizations Control Table */}
          <Card
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                <span style={{ fontWeight: 700, fontSize: 16 }}>Organization Biometric Sync Status</span>
                <Input
                  prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
                  placeholder="Search organization by name, ID or phone..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ width: 320, borderRadius: 8 }}
                  allowClear
                />
              </div>
            }
            style={{ borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}
          >
            {/* Bulk Action Controls */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 12,
                padding: '12px 16px',
                background: selectedRowKeys.length ? '#eff6ff' : '#f8fafc',
                borderRadius: 8,
                marginBottom: 16,
                border: selectedRowKeys.length ? '1px solid #bfdbfe' : '1px solid #e2e8f0'
              }}
            >
              <div>
                {selectedRowKeys.length > 0 ? (
                  <Text strong style={{ color: '#1d4ed8' }}>
                    {selectedRowKeys.length} of {organizations.length} organizations selected
                  </Text>
                ) : (
                  <Text type="secondary">
                    Select organizations using checkboxes to perform bulk actions (e.g. Activate or Push Credentials)
                  </Text>
                )}
              </div>

              <Space wrap>
                <Popconfirm
                  title="Activate ZKTeco sync for selected organizations?"
                  description="All selected organizations will have biometric sync enabled with current server credentials."
                  onConfirm={() => handleBulkAction('activate')}
                  disabled={!selectedRowKeys.length || bulkLoading}
                  okText="Yes, Activate"
                  cancelText="Cancel"
                >
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    disabled={!selectedRowKeys.length}
                    loading={bulkLoading}
                    style={{ background: '#16a34a', borderColor: '#16a34a', borderRadius: 8 }}
                  >
                    Activate Selected
                  </Button>
                </Popconfirm>

                <Popconfirm
                  title="Deactivate ZKTeco sync for selected organizations?"
                  description="Selected organizations will stop syncing biometric attendance."
                  onConfirm={() => handleBulkAction('deactivate')}
                  disabled={!selectedRowKeys.length || bulkLoading}
                  okText="Yes, Deactivate"
                  cancelText="Cancel"
                >
                  <Button
                    danger
                    icon={<CloseCircleOutlined />}
                    disabled={!selectedRowKeys.length}
                    loading={bulkLoading}
                    style={{ borderRadius: 8 }}
                  >
                    Deactivate Selected
                  </Button>
                </Popconfirm>

                <Popconfirm
                  title="Apply Master Server Credentials to selected organizations?"
                  description="The current Master URL, Username, and Password will be pushed to the selected organizations."
                  onConfirm={() => handleBulkAction('applyCredentials')}
                  disabled={!selectedRowKeys.length || bulkLoading}
                  okText="Yes, Push Credentials"
                  cancelText="Cancel"
                >
                  <Button
                    icon={<SyncOutlined />}
                    disabled={!selectedRowKeys.length}
                    loading={bulkLoading}
                    style={{ borderRadius: 8 }}
                  >
                    Apply Master Credentials
                  </Button>
                </Popconfirm>
              </Space>
            </div>

            <Table
              rowKey="id"
              rowSelection={rowSelection}
              columns={columns}
              dataSource={filteredOrgs}
              loading={loading}
              pagination={{ pageSize: 15, showTotal: (total) => `Total ${total} organizations` }}
              locale={{ emptyText: 'No organizations found' }}
            />
          </Card>
        </Content>
      </Layout>

      <ChangePasswordModal open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} />
    </Layout>
  );
}
